import React, { useState } from "react";
import { ChatterExpressDialog } from "./ChatterExpressDialog";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { MessageSquare, Camera, AlertTriangle, CheckCircle2, ChevronRight, Sparkles, UserRound, Send } from "lucide-react";

/**
 * One job's AI day summary. `compact` renders the trimmed Home version.
 */
export function DailySummaryCard({ summary, compact = false }) {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const [chatter, setChatter] = useState(false);
  const text = lang === "pt" ? summary.summary_pt : summary.summary_en;
  const highlights = (lang === "pt" ? summary.highlights_pt : summary.highlights_en) || [];
  const st = summary.stats || {};
  return (
    <Card
      data-testid={`summary-card-${summary.job_number}`}
      className={`${compact ? "p-3" : "p-4"} cursor-pointer hover:bg-muted/30 transition-colors`}
      onClick={() => navigate(compact ? "/summary" : `/jobs/${summary.job_id}`)}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {summary.crew_code && (
          <Badge className="bg-primary text-primary-foreground font-mono-plex text-[12px] leading-none tracking-wider px-2 py-1 rounded-md shrink-0">
            {summary.crew_code}
          </Badge>
        )}
        {summary.supervisor_name && (
          <Badge data-testid="summary-supervisor-badge" variant="outline" className="text-[10px] px-1.5 py-0.5 flex items-center gap-1 border-primary/40 text-primary font-medium shrink-0">
            <UserRound className="h-2.5 w-2.5" /> {summary.supervisor_name}
          </Badge>
        )}
        <span className="font-mono-plex text-xs font-semibold text-primary">#{summary.job_number}</span>
        <span className="text-sm font-medium truncate flex-1 min-w-0">{summary.customer_name}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-muted-foreground" data-testid="summary-stats">
        <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {st.messages || 0}</span>
        <span className="flex items-center gap-1"><Camera className="h-3 w-3" /> {st.photos || 0}</span>
        {/* Only issues STILL OPEN show in red — if everything opened that day was
            already resolved, only the green "resolved" counter appears */}
        {(() => {
          const openNow = st.issues_open_now ?? Math.max(0, (st.issues_opened || 0) - (st.issues_resolved || 0));
          return openNow > 0 ? (
            <span className="flex items-center gap-1 text-destructive font-medium" data-testid="summary-open-issues">
              <AlertTriangle className="h-3 w-3" /> {openNow} {t("issues_opened_label")}
            </span>
          ) : null;
        })()}
        {(st.issues_resolved || 0) > 0 && (
          <span className="flex items-center gap-1 text-[hsl(var(--success))] font-medium" data-testid="summary-resolved-issues">
            <CheckCircle2 className="h-3 w-3" /> {st.issues_resolved} {t("issues_resolved_label")}
          </span>
        )}
      </div>
      <p className={`text-sm mt-2 leading-relaxed ${compact ? "line-clamp-2" : ""}`} data-testid="summary-text">
        <Sparkles className="h-3.5 w-3.5 inline mr-1 text-accent-foreground align-[-2px]" />
        {text}
      </p>
      {!compact && highlights.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="summary-highlights">
          {highlights.map((h, i) => (
            <li key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
              <span className="h-1 w-1 rounded-full bg-primary mt-1.5 shrink-0" />
              <span>{h}</span>
            </li>
          ))}
        </ul>
      )}
      {!compact && user?.role !== "installer" && (
        <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            data-testid={`chatter-open-${summary.job_number}`}
            onClick={(e) => { e.stopPropagation(); setChatter(true); }}
            className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-semibold text-primary hover:bg-primary/10 active:scale-[0.98] transition-colors"
          >
            <Send className="h-3.5 w-3.5" /> Chatter Express
          </button>
          {chatter && (
            <ChatterExpressDialog open={chatter} onOpenChange={setChatter} jobId={summary.job_id} date={summary.date}
              jobLabel={`#${summary.job_number} — ${summary.customer_name}`} />
          )}
        </div>
      )}
    </Card>
  );
}
